import type { JourneyCanvas } from '@/api/marketing.api';

export type JourneyNodeType = 'TRIGGER' | 'ACTION' | 'DELAY' | 'GOAL';

export type JourneyStep = {
  key: string;
  type: JourneyNodeType;
  label: string;
  delayAmount?: number;
  delayUnit?: 'hours' | 'days';
  actionKind?: 'EMAIL' | 'SMS' | 'TAG';
};

export const JOURNEY_NODE_TYPES: { value: JourneyNodeType; label: string }[] = [
  { value: 'TRIGGER', label: 'Trigger' },
  { value: 'ACTION', label: 'Action' },
  { value: 'DELAY', label: 'Delay / wait' },
  { value: 'GOAL', label: 'Goal' },
];

export const JOURNEY_ACTION_KINDS = [
  { value: 'EMAIL', label: 'Send email' },
  { value: 'SMS', label: 'Send SMS' },
  { value: 'TAG', label: 'Apply tag' },
] as const;

export function parseCanvasJson(text?: string | null): JourneyCanvas | null {
  if (!text?.trim()) return null;
  try {
    const parsed = JSON.parse(text) as JourneyCanvas;
    if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function canvasToJson(canvas: JourneyCanvas): string {
  return JSON.stringify(canvas, null, 2);
}

export function createDefaultCanvas(trigger: string): JourneyCanvas {
  return {
    nodes: [
      { key: 'start', type: 'TRIGGER', label: trigger, x: 40, y: 80 },
      { key: 'email', type: 'ACTION', label: 'Send email', x: 220, y: 80 },
      { key: 'wait', type: 'DELAY', label: 'Wait 2 days', x: 400, y: 80 },
      { key: 'goal', type: 'GOAL', label: 'Converted', x: 580, y: 80 },
    ],
    edges: [
      { from: 'start', to: 'email' },
      { from: 'email', to: 'wait' },
      { from: 'wait', to: 'goal' },
    ],
  };
}

function parseDelayFromLabel(label: string): { delayAmount?: number; delayUnit?: 'hours' | 'days' } {
  const match = label.match(/wait\s+(\d+)\s*(day|days|hour|hours)/i);
  if (!match) return {};
  const amount = Number(match[1]);
  const unit = match[2].toLowerCase().startsWith('hour') ? 'hours' : 'days';
  return { delayAmount: amount, delayUnit: unit };
}

function parseActionFromLabel(label: string): JourneyStep['actionKind'] | undefined {
  const lower = label.toLowerCase();
  if (lower.includes('sms')) return 'SMS';
  if (lower.includes('tag')) return 'TAG';
  if (lower.includes('email')) return 'EMAIL';
  return undefined;
}

export function stepsFromCanvas(canvas: JourneyCanvas): JourneyStep[] {
  const ordered = orderNodesLinearly(canvas);
  return ordered.map((node) => {
    const delay = node.type === 'DELAY' ? parseDelayFromLabel(node.label) : {};
    const actionKind = node.type === 'ACTION' ? parseActionFromLabel(node.label) : undefined;
    return {
      key: node.key,
      type: node.type as JourneyNodeType,
      label: node.label,
      ...delay,
      actionKind,
    };
  });
}

export function canvasFromSteps(steps: JourneyStep[]): JourneyCanvas {
  const nodes = steps.map((step, index) => {
    let label = step.label.trim();
    if (step.type === 'DELAY' && step.delayAmount && step.delayUnit) {
      label = `Wait ${step.delayAmount} ${step.delayUnit}`;
    } else if (step.type === 'ACTION' && step.actionKind) {
      const kindLabel = JOURNEY_ACTION_KINDS.find((k) => k.value === step.actionKind)?.label ?? 'Action';
      label = label || kindLabel;
    }
    return {
      key: step.key,
      type: step.type,
      label: label || step.type,
      x: 40 + index * 180,
      y: 80,
    };
  });
  const edges = nodes.slice(0, -1).map((node, index) => ({
    from: node.key,
    to: nodes[index + 1].key,
  }));
  return { nodes, edges };
}

/** Order nodes by edge chain starting from first TRIGGER or first node. */
export function orderNodesLinearly(canvas: JourneyCanvas): JourneyCanvas['nodes'] {
  if (canvas.nodes.length === 0) return [];
  const byKey = new Map(canvas.nodes.map((n) => [n.key, n]));
  const outgoing = new Map<string, string>();
  for (const edge of canvas.edges) {
    if (!outgoing.has(edge.from)) outgoing.set(edge.from, edge.to);
  }
  const start =
    canvas.nodes.find((n) => n.type === 'TRIGGER')?.key ?? canvas.nodes[0]?.key;
  const ordered: JourneyCanvas['nodes'] = [];
  const visited = new Set<string>();
  let current: string | undefined = start;
  while (current && byKey.has(current) && !visited.has(current)) {
    visited.add(current);
    ordered.push(byKey.get(current)!);
    current = outgoing.get(current);
  }
  for (const node of canvas.nodes) {
    if (!visited.has(node.key)) ordered.push(node);
  }
  return ordered;
}

export function newStepKey(type: JourneyNodeType, existing: JourneyStep[]): string {
  const base = type.toLowerCase();
  let i = existing.filter((s) => s.type === type).length + 1;
  let key = `${base}-${i}`;
  while (existing.some((s) => s.key === key)) {
    i += 1;
    key = `${base}-${i}`;
  }
  return key;
}

export function defaultStepLabel(type: JourneyNodeType, trigger?: string): string {
  switch (type) {
    case 'TRIGGER':
      return trigger ?? 'Entry trigger';
    case 'ACTION':
      return 'Send email';
    case 'DELAY':
      return 'Wait 2 days';
    case 'GOAL':
      return 'Converted';
    default:
      return type;
  }
}

export function validateCanvas(canvas: JourneyCanvas): string | null {
  if (canvas.nodes.length === 0) return 'Add at least one step.';
  if (!canvas.nodes.some((n) => n.type === 'TRIGGER')) return 'Include a trigger step at the start.';
  if (canvas.nodes[0]?.type !== 'TRIGGER') return 'The first step must be a trigger.';
  const keys = new Set<string>();
  for (const node of canvas.nodes) {
    if (!node.key.trim()) return 'Every step needs an identifier.';
    if (keys.has(node.key)) return 'Step identifiers must be unique.';
    keys.add(node.key);
    if (!node.label.trim()) return 'Every step needs a label.';
  }
  return null;
}
